// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { FC } from 'react';
import { forwardRef } from 'react';
import {
  MaterialDesignContent,
  SnackbarProvider as NotistackSnackbarProvider,
} from 'notistack';
import type {
  CustomContentProps,
  SnackbarProviderProps as NotistackSnackbarProviderProps,
} from 'notistack';
import {
  SyncFailedSnackbar,
  SyncingSnackbar,
  SyncingTagsSnackbar,
  UploadingDocumentSnackbar,
} from '@shared/ui-kit/components/syncing-snackbar';

/**
 * Snackbar variant background colors aligned with tailwind.config.js theme:
 *
 * - Default: primary-medium (#79828D)
 * - Success: positive / primary.green (#0D9795)
 * - Error: negative / primary.red (#D80027)
 * - Warning: pending (#FFC400)
 * - Info: primary (#1072BA)
 */
const SNACKBAR_VARIANT_BG: Record<string, string> = {
  default: '#79828D', // primary-medium
  success: '#0D9795', // positive / primary.green
  error: '#D80027', // negative / primary.red
  warning: '#FFC400', // pending
  info: '#1072BA', // primary.DEFAULT
};

/** Default auto-hide for transient snackbars (non-persist). */
export const SNACKBAR_AUTO_HIDE_DURATION = 2500;

const DEFAULT_ANCHOR_ORIGIN: NotistackSnackbarProviderProps['anchorOrigin'] = {
  vertical: 'bottom',
  horizontal: 'left',
};

function createVariantContent(variant: string) {
  const backgroundColor =
    SNACKBAR_VARIANT_BG[variant] ?? SNACKBAR_VARIANT_BG.default;
  const Component = forwardRef<HTMLDivElement, CustomContentProps>((props, ref) => (
    <MaterialDesignContent
      {...props}
      ref={ref}
      style={{ ...props.style, backgroundColor }}
    />
  ));
  Component.displayName = `VariantSnackbarContent(${variant})`;
  return Component;
}

const DefaultSnackbarContent = createVariantContent('default');
const SuccessSnackbarContent = createVariantContent('success');
const ErrorSnackbarContent = createVariantContent('error');
const WarningSnackbarContent = createVariantContent('warning');
const InfoSnackbarContent = createVariantContent('info');

export interface SnackbarProviderProps extends NotistackSnackbarProviderProps {
  /** Override default anchor origin. */
  anchorOrigin?: NotistackSnackbarProviderProps['anchorOrigin'];
}

/**
 * Snackbar provider with app-default configuration. Transient snackbars use
 * {@link SNACKBAR_AUTO_HIDE_DURATION} (2500ms) unless overridden. All variants
 * (default, success, error, warning, info) use colors from tailwind.config.js. For
 * syncing notifications: enqueueSnackbar(message, { variant: 'syncing', persist:
 * true }). For tags sync: enqueueSnackbar(message, { variant: 'syncingTags',
 * persist: true }). On sync failure: enqueueSnackbar(message, { variant:
 * 'syncingFailed' }). For document upload: enqueueSnackbar(message, { variant:
 * 'uploadingDocument', persist: true }).
 */
export const SnackbarProvider: FC<SnackbarProviderProps> = ({
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  Components: componentsProp,
  ...rest
}) => {
  const Components = {
    default: DefaultSnackbarContent,
    success: SuccessSnackbarContent,
    error: ErrorSnackbarContent,
    warning: WarningSnackbarContent,
    info: InfoSnackbarContent,
    syncing: SyncingSnackbar,
    syncingTags: SyncingTagsSnackbar,
    syncingFailed: SyncFailedSnackbar,
    uploadingDocument: UploadingDocumentSnackbar,
    ...componentsProp,
  };

  return (
    <NotistackSnackbarProvider
      anchorOrigin={anchorOrigin}
      autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
      Components={Components}
      {...rest}
    />
  );
};

SnackbarProvider.displayName = 'SnackbarProvider';
